import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Logo } from "@/components/Logo";
import { Card } from "@/components/dash";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Store, ShoppingBag, CalendarClock, ClipboardList, CheckCircle2, XCircle, LogOut, Loader2 } from "lucide-react";

export default function Admin() {
  const { logout } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);

  useEffect(() => { api.get("/admin/overview").then((r) => setData(r.data)); }, []);

  if (!data) return <div className="flex min-h-screen items-center justify-center bg-cream"><Loader2 className="h-8 w-8 animate-spin text-terracotta" /></div>;

  const stats = [
    [Store, "Total bakeries", data.total_bakeries],
    [ShoppingBag, "With products", data.bakeries_with_products],
    [CalendarClock, "With a bake day", data.bakeries_with_bakeday],
    [ClipboardList, "With orders", data.bakeries_with_orders],
    [ClipboardList, "Total orders", data.total_orders],
  ];

  return (
    <div className="min-h-screen bg-cream paper-grain">
      <header className="border-b border-cocoa/10 bg-warmwhite/90 backdrop-blur-xl">
        <div className="mx-auto flex max-w-6xl items-center justify-between px-5 py-4">
          <div className="flex items-center gap-3"><Logo /><span className="rounded-full bg-cocoa px-3 py-1 text-xs font-medium text-warmwhite">Founder admin</span></div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => navigate("/dashboard")} className="rounded-full border-cocoa/20 text-cocoa">My dashboard</Button>
            <Button variant="ghost" onClick={async () => { await logout(); navigate("/"); }} className="rounded-full text-cocoa"><LogOut className="mr-1.5 h-4 w-4" /> Sign out</Button>
          </div>
        </div>
      </header>

      <div className="mx-auto max-w-6xl px-5 py-8">
        <h1 className="font-serif text-3xl font-semibold text-cocoa">Platform overview</h1>
        <p className="mt-1 text-sm text-cocoa/60">Identify and reach out to your early bakery signups.</p>

        <div className="mt-6 grid gap-4 sm:grid-cols-3 lg:grid-cols-5">
          {stats.map(([Icon, label, value]) => (
            <Card key={label} className="p-5">
              <Icon strokeWidth={1.5} className="mb-2 h-5 w-5 text-terracotta" />
              <p className="font-serif text-3xl leading-none text-cocoa">{value}</p>
              <p className="mt-1.5 text-xs text-cocoa/50">{label}</p>
            </Card>
          ))}
        </div>

        {data.signups_by_date.length > 0 && (
          <Card className="mt-6">
            <h2 className="mb-3 font-serif text-lg text-cocoa">Signups by date</h2>
            <div className="flex flex-wrap gap-2">
              {data.signups_by_date.map((s) => (
                <div key={s.date} className="rounded-xl bg-cream px-3 py-2 text-sm"><span className="text-cocoa/60">{s.date}</span> <span className="font-medium text-cocoa">· {s.count}</span></div>
              ))}
            </div>
          </Card>
        )}

        <Card className="mt-6 overflow-x-auto p-0">
          <div className="p-5 pb-0"><h2 className="font-serif text-lg text-cocoa">Bakery owners</h2></div>
          <Table>
            <TableHeader>
              <TableRow className="border-cocoa/10 hover:bg-transparent">
                <TableHead className="text-cocoa/60">Bakery</TableHead>
                <TableHead className="text-cocoa/60">Owner</TableHead>
                <TableHead className="text-cocoa/60">Email</TableHead>
                <TableHead className="text-cocoa/60">Products</TableHead>
                <TableHead className="text-cocoa/60">Bake day</TableHead>
                <TableHead className="text-cocoa/60">Orders</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {data.bakeries.map((b, i) => (
                <TableRow key={i} className="border-cocoa/5" data-testid={`admin-bakery-${i}`}>
                  <TableCell className="font-medium text-cocoa">{b.bakery_name}{b.is_demo && <span className="ml-2 rounded-full bg-sage/20 px-2 py-0.5 text-xs text-cocoa/60">demo</span>}</TableCell>
                  <TableCell className="text-cocoa/70">{b.owner_name}</TableCell>
                  <TableCell className="text-sm text-cocoa/70">{b.owner_email}</TableCell>
                  <TableCell>{b.has_products ? <CheckCircle2 className="h-4 w-4 text-sage" /> : <XCircle className="h-4 w-4 text-cocoa/20" />}</TableCell>
                  <TableCell>{b.has_bakeday ? <CheckCircle2 className="h-4 w-4 text-sage" /> : <XCircle className="h-4 w-4 text-cocoa/20" />}</TableCell>
                  <TableCell className="text-cocoa">{b.order_count}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
      </div>
    </div>
  );
}
